import { getGlobalHandle } from '@getaccept/lib-shared/src/helpers';
import { LocalStorageKey } from '@getaccept/lib-shared/src/enums/local-storage';
import * as LocalStorageHelper from '@getaccept/lib-shared/src/helpers/local-storage.helper';
import type { IdentityProvider } from '../../signup/enums/identity-provider';
import type { ProfileInformation } from '../../verify-user/types/profile-information';
import type { MqlData } from '../../create-entity/types/create-entity-form-data';

export const useGoogleTagManager = () => {
  const global = getGlobalHandle();

  const push = async (event: string, data: Record<string, unknown>) => {
    global.dataLayer = global.dataLayer || [];
    global.dataLayer.push({ event, ...data });
  };

  const getDataFromStorage = () => {
    const localData = LocalStorageHelper.getLocalStorage(LocalStorageKey.SignupAnalyticsData);
    const { signupMethod } = localData ? JSON.parse(localData) : {};
    return {
      signupMethod,
      email:
        signupMethod?.email ??
        LocalStorageHelper.getLocalStorage(LocalStorageKey.VerificationEmail),
    };
  };

  const initiateSignup = async ({
    email,
    signupMethod,
  }: {
    email?: string;
    signupMethod?: 'email' | IdentityProvider;
  }) => {
    const hash = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(email));
    const contact_id = btoa(String.fromCharCode(...Array.from(new Uint8Array(hash))));

    push('signup_form_submit', {
      lead_type: 'free_account',
      contact_id,
      signup_method: signupMethod || 'email',
    });
  };

  const initiateSignupError = async ({
    email,
    signupMethod,
    error,
  }: {
    error?: string;
    email?: string;
    signupMethod?: 'email' | IdentityProvider;
  }) => {
    const hash = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(email));
    const contact_id = btoa(String.fromCharCode(...Array.from(new Uint8Array(hash))));

    push('signup_form_submit_error', {
      error: error,
      lead_type: 'free_account',
      contact_id,
      signup_method: signupMethod || 'email',
      ...getDataFromStorage(),
    });
  };

  const verifyEmail = async (userId: string, isInvitation: boolean) => {
    push('signup_verify_email', {
      user_id: userId,
      ...getDataFromStorage(),
      is_invitation: isInvitation,
    });
  };

  const addUserInfo = async (userId: string, userInfo: Omit<ProfileInformation, 'password'>) => {
    const { firstName, lastName, phoneNumber, language } = userInfo;
    push('signup_user_info', {
      user_id: userId,
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      language,
      ...getDataFromStorage(),
    });
  };

  type CreateEntityParams = MqlData & {
    userId: string;
    companyName: string;
    country: string;
  };

  const createEntity = async ({
    userId,
    salesTeamSize,
    crmType,
    companyName,
    awarenessInformation,
    country,
  }: CreateEntityParams) => {
    push('signup_user_info_added', {
      crm_type: crmType,
      company_name: companyName,
      user_id: userId,
      sales_team_size: salesTeamSize,
      awareness_information: awarenessInformation,
      country,
      ...getDataFromStorage(),
    });
  };

  return { initiateSignup, initiateSignupError, addUserInfo, verifyEmail, createEntity };
};
